/**
 * DomainField — domaine associé à une cuvée, modifiable au clic/survol.
 * Autocomplétion depuis les domaines connus (lien automatique) ou saisie libre (autre domaine,
 * créé au vol). Réutilisable : fiche bouteille, dégustation, etc. — pas de duplication.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Pencil, Plus } from 'lucide-react';
import { caveApi, vignoblesApi } from '../../services/api';

export function DomainField({ cuveeId, domainName, linkTo, linkState, onLinked, testId = 'domain',
  placeholder = 'Ajouter un domaine', displayClassName = 'font-title text-xl text-gold700' }) {
  const [editing, setEditing] = useState(false);
  const [q, setQ] = useState('');
  const { data: domains = [] } = useQuery({ queryKey: ['domains'], queryFn: () => vignoblesApi.domains(), enabled: editing });
  const matches = domains.filter((d) => d.name.toLowerCase().includes(q.toLowerCase())).slice(0, 8);

  const link = async (payload) => {
    setEditing(false); setQ('');
    try { const dom = await caveApi.setCuveeDomain(cuveeId, payload); onLinked?.(dom); toast.success('Domaine lié'); }
    catch (e) { toast.error(e.message || 'Échec du lien'); }
  };

  if (!editing) {
    return (
      <span className="group inline-flex items-center gap-1.5">
        {domainName ? (
          linkTo
            ? <Link to={linkTo} state={linkState} className={`${displayClassName} hover:underline`} data-testid={`${testId}-display`}>{domainName} →</Link>
            : <span className={displayClassName} data-testid={`${testId}-display`}>{domainName}</span>
        ) : (
          <button onClick={() => { setEditing(true); setQ(''); }} className="font-title text-lg text-gold700 hover:underline flex items-center gap-1" data-testid={`${testId}-add`}>
            <Plus className="w-4 h-4" /> {placeholder}
          </button>
        )}
        {domainName && (
          <button onClick={() => { setEditing(true); setQ(''); }} title="Modifier le domaine" data-testid={`${testId}-edit`}
            className="opacity-0 group-hover:opacity-70 text-gold700 transition-opacity"><Pencil className="w-3.5 h-3.5" /></button>
        )}
      </span>
    );
  }
  return (
    <span className="relative inline-block">
      <input autoFocus className="vd-input h-8 w-64 text-[13px]" placeholder="Chercher ou saisir un domaine…" value={q}
        onChange={(e) => setQ(e.target.value)} onBlur={() => setTimeout(() => setEditing(false), 200)}
        onKeyDown={(e) => { if (e.key === 'Enter' && q.trim()) link({ name: q.trim() }); if (e.key === 'Escape') setEditing(false); }}
        data-testid={`${testId}-input`} />
      <div className="absolute z-30 mt-1 bg-paper border border-hair rounded shadow-md w-64 max-h-56 overflow-auto" data-testid={`${testId}-menu`}>
        {matches.map((d) => (
          <button key={d.id} onMouseDown={() => link({ domain_id: d.id })} className="block w-full text-left px-2.5 py-1.5 text-[13px] hover:bg-surface" data-testid={`${testId}-opt-${d.id}`}>
            {d.name} <span className="text-n500">· {d.region || '—'}</span>
          </button>
        ))}
        {q.trim() && !matches.some((d) => d.name.toLowerCase() === q.trim().toLowerCase()) && (
          <button onMouseDown={() => link({ name: q.trim() })} className="flex items-center gap-1 w-full text-left px-2.5 py-1.5 text-[13px] hover:bg-surface text-gold700 border-t border-hair" data-testid={`${testId}-create`}>
            <Plus className="w-3.5 h-3.5" /> Autre domaine : « {q.trim()} »
          </button>
        )}
      </div>
    </span>
  );
}
